(() => {
  const SUMMARY_STATE_KEY = '__cartDrawerV2SummaryState';
  const INSTANCE_KEY = '__cartDrawerV2Summary';

  if (window[INSTANCE_KEY]) {
    return;
  }

  class CartDrawerV2Summary {
    constructor() {
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.state = window[SUMMARY_STATE_KEY] || { detailsOpen: false };
      window[SUMMARY_STATE_KEY] = this.state;
    }

    mount() {
      // sync() below bails when liquid-ajax-cart has not finished initialising, and
      // queue-end/cart:updated only fire once the cart is mutated — so without this
      // the first reconciliation was the user's first cart change. If init has
      // already fired, sync() picks the cart up directly and this listener is inert.
      document.addEventListener('liquid-ajax-cart:init', this.handleCartUpdate);
      document.addEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.addEventListener('cart:updated', this.handleCartUpdate);
      document.addEventListener('click', this.handleDocumentClick);
      this.sync();
    }

    handleCartUpdate() {
      this.sync();
    }

    handleDocumentClick(event) {
      const toggle = event.target.closest('.cart-drawer-v2__totals__total__toggle');

      if (!toggle) {
        return;
      }

      window.requestAnimationFrame(() => {
        const details = document.querySelector('#CartDrawerV2 .cart-drawer-v2__totals__details');
        this.state.detailsOpen = details ? !details.classList.contains('hidden') : false;
      });
    }

    sync() {
      const cartDrawer = document.querySelector('#CartDrawerV2');

      if (cartDrawer) {
        this.restoreDetailsState(cartDrawer);
      }

      const cart = this.getCart();

      if (!cart) {
        return;
      }

      if (cartDrawer) {
        this.updateFinanceSummary(cartDrawer, cart);
      }
    }

    getCart() {
      return window.liquidAjaxCart?.getState?.()?.cart || window.liquidAjaxCart?.cart || null;
    }

    getThemeState() {
      const themeState = window.__Theme?._ctx?.getState?.();

      if (themeState) {
        return themeState;
      }

      // window.__Theme is only assigned at the very end of Theme.mountComponents(),
      // so on first load this runs before it exists and the theme state reads
      // back empty. Both fallbacks are inline JSON rendered in <head> (snippets/
      // scripts.liquid and snippets/theme-information.liquid), so they are already
      // populated by the time any deferred script executes.
      if (window.radiant) {
        return window.radiant;
      }

      if (this.themeInformation === undefined) {
        const raw = document.querySelector('[data-theme-information]')?.textContent;

        try {
          this.themeInformation = raw ? JSON.parse(raw) : null;
        } catch (error) {
          console.warn('[cart-drawer-v2-summary] Could not parse theme information', error);
          this.themeInformation = null;
        }
      }

      return this.themeInformation;
    }

    restoreDetailsState(cartDrawer) {
      const details = cartDrawer.querySelector('.cart-drawer-v2__totals__details');
      const showDetails = cartDrawer.querySelector('.cart-drawer-v2__totals__total__toggle .show-details');
      const hideDetails = cartDrawer.querySelector('.cart-drawer-v2__totals__total__toggle .hide-details');

      if (!details || !showDetails || !hideDetails) {
        return;
      }

      details.classList.toggle('hidden', !this.state.detailsOpen);
      showDetails.classList.toggle('hidden', this.state.detailsOpen);
      hideDetails.classList.toggle('hidden', !this.state.detailsOpen);
    }

    updateFinanceSummary(cartDrawer, cart) {
      const finance = window.radiant?.finance;
      const financeWrapper = cartDrawer.querySelector('.cart-drawer-v2__totals__finance');
      const financePrice = financeWrapper?.querySelector('.finance-price');
      const trigger = financeWrapper?.querySelector('[data-finance-block-price]');

      // Until Theme.js has initialised Finance there is nothing to quote with, and treating
      // that as ineligible would hide a row Liquid has already rendered correctly. Leave the
      // server-rendered state be and wait for a sync that can quote.
      if (!finance?.getProvider?.() || !financeWrapper || !financePrice) {
        return;
      }

      const totalPrice = Number(cart.total_price || 0);
      const best = finance.best(totalPrice);

      financeWrapper.classList.toggle('hidden', !best);

      // Carried on the trigger so the calculator popup opens on the current cart total
      if (trigger) {
        trigger.setAttribute('data-finance-block-price', Math.round(totalPrice));
      }

      if (!best) {
        return;
      }

      financePrice.textContent = `${finance.formatMoney(best.monthly)}/month`;
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        const moneyFormat = Number(cents || 0) % 100 === 0
          ? '£{{amount_no_decimals}}'
          : (this.getThemeState()?.moneyFormat || window.Shopify.money_format || '£{{amount}}');

        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      const value = (Number(cents || 0) / 100).toFixed(2);
      const [whole, fraction] = value.split('.');
      const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return fraction === '00' ? `£${formattedWhole}` : `£${formattedWhole}.${fraction}`;
    }
  }

  const summary = new CartDrawerV2Summary();
  window[INSTANCE_KEY] = summary;
  summary.mount();
})();
